import { pointOnPage } from '@/lib/pageGeometry';
import { trackPointerDrag } from '@/lib/pointerDrag';
import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate, useDocumentWorkspace } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import Toolbar from '@/components/Toolbar';
import PageOrganizer from '@/components/PageOrganizer';
import ImageProperties from '@/components/ImageProperties';
import ThumbnailSidebar from '@/components/ThumbnailSidebar';
import PageCanvas from '@/components/PageCanvas';
import ScrollablePages from '@/components/ScrollablePages';
import PdfAnnotationLayer from '@/components/PdfAnnotationLayer';
import PdfTextLayer from '@/components/PdfTextLayer';
import PdfLinkLayer from '@/components/PdfLinkLayer';
import FormToolbar from '@/components/FormToolbar';
import AnnotationLayer from '@/components/AnnotationLayer';
import EditToolbar from '@/components/EditToolbar';
import EditLayer from '@/components/EditLayer';
import MaskLayer from '@/components/MaskLayer';
import FindBar from '@/components/FindBar';
import SearchHighlightLayer from '@/components/SearchHighlightLayer';

import UnsavedWarningDialog from '@/components/UnsavedWarningDialog';
import NavGuardContext from '@/contexts/NavGuardContext';
import FlattenConfirmDialog from '@/components/FlattenConfirmDialog';
import SignaturePanel from '@/components/SignaturePanel';
import usePDF from '@/hooks/usePDF';
import useFormFields from '@/hooks/useFormFields';
import useContentRemovalPreview from '@/hooks/useContentRemovalPreview';
import useAnnotations from '@/hooks/useAnnotations';
import useEdits from '@/hooks/useEdits';
import { usePrecomputedSave } from '@/hooks/usePrecomputedSave';
import { useSavedSignatures } from '@/hooks/useSavedSignatures';
import { saveViaWorker } from '@/lib/saveWorkerClient';
import { hasDestructiveTextEdits } from '@/lib/saveTarget';
import { saveDocumentSnapshot, pdfFileName } from '@/lib/documentSave';
import { splitPDF } from '@/lib/pdfOperations';
import {
  planDeleteExecution,
  planExtractExecution,
  buildUndoSummary,
  normalizeAiLevel,
  normalizeAiFormat,
} from '@/lib/aiAction';
import { isDesktop, updateTitle } from '@/lib/fileSystem';
import { useFileDialog } from '@/hooks/useFileDialog';
import { viewerSaveRef } from '@/App';
import { detectTextContent, detectImageContent } from '@/lib/contentDetector';
import { blockToEdit, visibleCandidates } from '@/lib/detectedEditFactory';
import { createDocumentSearch, filterMatchesByDeletedRegions } from '@/lib/documentSearch';
import { pdfjsLib } from '@/lib/pdfWorker';
import { Loader2, FileText, Check, Sparkles } from 'lucide-react';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';
import AiPanel from '../components/ai/AiPanel';
import { useAuth } from '@/hooks/useAuth';
import { getCloudState, getCloudUpsell } from '@/lib/cloudUpsell';
import useAutoSaveToCloud from '@/hooks/useAutoSaveToCloud';
import { recordRecent } from '@/lib/recentFiles';
import { printDocument } from '@/lib/printDocument';
import { itemFingerprint as flagsOf, isUntouchedDetection } from '@/lib/documentChanges';

// Pure helpers for the dirty-state tracker — serialize an item's deleted/
// modified flags into a short tag so the synced snapshot map can be compared
// cheaply across renders.
const annotKey = (a) => a.groupId || a.id;

/**
 * Shape the detected form fields + current values into the `fieldData` array
 * saveAllChanges consumes. One function, used by both the direct save and the
 * background pre-compute — writing it twice is how the two drifted before and
 * silently dropped every /FreeText value for want of a `source` key.
 *
 * `annotId` (PDF.js's object reference, e.g. "34R") is what lets the save
 * pipeline tell apart widgets that share a field name — the exemption
 * certificate's section 3 is one /Btn field with 20 kid checkboxes all named
 * "Item.Checkbox". `source`/`pageNum`/`rect` route /FreeText placeholders to
 * the drawText branch. Pure + exported so it can be unit-tested without a
 * full Viewer render harness.
 */
export function toFieldData(formFields, formValues) {
  return (formFields || []).map((f) => ({
    name: f.name,
    type: f.type,
    value: formValues?.[f.id],
    exportValue: f.exportValue,
    annotId: f.annotId,
    source: f.source || 'widget',
    pageNum: f.pageNum,
    rect: f.rect,
  }));
}

/**
 * Gate for the whole-document image-detection sweep (Task 7 Critical fix).
 * The sweep must run on FIRST IMAGE-TOOL USE — a deliberate user action —
 * never merely because Edit Text was opened. `imageToolUsed` is true once
 * editTool has ever been 'image' (EditToolbar's "Add Image" button sets it
 * before opening the file picker); `alreadyDone` guards the sweep to once
 * per document; `hasFileBytes` guards against running before the background
 * file-bytes clone (fileArrayBufferRef) has landed.
 * Pure + exported so it can be unit-tested without a full Viewer render harness.
 */
export function shouldRunImageSweep({ imageToolUsed, alreadyDone, hasFileBytes }) {
  if (!imageToolUsed || alreadyDone) return false;
  return !!hasFileBytes;
}

/**
 * Build the per-page map of deleted (tombstoned) detected-block regions used
 * to filter find-in-document results (Fix 2 — find must respect pending
 * deletions). A tombstoned detected block stays in `edits` with
 * `deleted: true` (see useEdits' hasBakedDrawing) so save can whiteout it;
 * its `originalBounds` is the coverage rect for that whiteout, which is also
 * exactly the region a find match must not resolve inside.
 * Pure + exported so it can be unit-tested without a full Viewer render harness.
 */
export function buildDeletedRegionsByPage(edits) {
  const map = new Map();
  for (const e of edits) {
    if (!e.deleted || e.source !== 'detected' || !e.originalBounds) continue;
    const list = map.get(e.pageNum) || [];
    list.push(e.originalBounds);
    map.set(e.pageNum, list);
  }
  return map;
}

/**
 * Re-run filterMatchesByDeletedRegions over an already-accumulated
 * `findResults` array when the deleted-regions map changes WHILE the find
 * bar is open. The original filter (see the streaming-search effect below)
 * only applies as fresh results land page-by-page — deleting a block while
 * find is already open and fully populated left its stale matches counted
 * and highlighted until the next search re-ran the whole scan.
 *
 * Returns a NEW (shorter) array when the filter actually drops something,
 * or `null` when nothing would change — the caller uses the null sentinel
 * to skip the setState entirely, which is what breaks the effect loop (a
 * setState to an equal-length array would otherwise re-trigger on every
 * dependency change even when there's nothing to drop).
 *
 * Drop-only: this never re-adds a match a previous deletion removed.
 * Undo/undelete is restored via the next re-search, not by this helper.
 *
 * Pure + exported so it can be unit-tested without a full Viewer render harness.
 */
export function refilterIfShrunk(results, deletedRegionsByPage) {
  if (!results || results.length === 0) return null;
  const filtered = filterMatchesByDeletedRegions(results, deletedRegionsByPage);
  if (filtered.length >= results.length) return null;
  return filtered;
}

/**
 * Viewer — Full PDF viewer with form filling, annotations, and editing.
 *
 * Layer order: Canvas → Form fields → Edit overlays → Annotation SVG
 * Edit mode activates both edit and annotation tools in a unified toolbar.
 */
export default function Viewer() {
  const location = useLocation();
  const navigate = useNavigate();
  const workspace = useDocumentWorkspace();
  const { t } = useTranslation('viewer');
  const { saveFile: saveFileDialog } = useFileDialog();
  const { user, account } = useAuth();
  const [currentFilePath, setCurrentFilePath] = useState(null); // Track disk path for direct save
  // Track the cloud doc id when this file was opened from cloud. Pages that
  // land here via /viewer with `location.state.cloudDocId` are "cloud-sourced"
  // and Save/⌘S writes back to cloud. `null` means device-sourced.
  const [cloudDocId, setCloudDocId] = useState(location.state?.cloudDocId || null);
  const [cloudSyncStatus, setCloudSyncStatus] = useState('synced'); // 'synced' | 'saving' | 'error'
  const scrollPagesRef = useRef(null);
  const singlePageContainerRef = useRef(null);
  const scrollTriggeredRef = useRef(false);
  const autoFittedDocRef = useRef(null);

  const {
    pdfDoc, numPages, currentPage, zoom, isFitted, isLargeFile, showThumbs, pageSize: displayedPageSize, pageSizes, maxPageWidth,
    loading, error, fileName, setFileName, sidecar, loadFile, goToPage, nextPage, prevPage, zoomIn, zoomOut, fitToWidth, resetZoom, setZoom, setIsFitted,
  } = usePDF();
  const geometryForPage = useCallback((pageNum) => pageSizes?.[pageNum] || displayedPageSize, [pageSizes, displayedPageSize]);
  const searchGeometryRef = useRef(geometryForPage);
  searchGeometryRef.current = geometryForPage;
  const currentGeometry = geometryForPage(currentPage);
  const pageSize = useMemo(() => ({ width: currentGeometry.baseWidth || currentGeometry.width, height: currentGeometry.baseHeight || currentGeometry.height }), [currentGeometry]);
  const [showPageOrganizer, setShowPageOrganizer] = useState(() => new URLSearchParams(location.search).get('panel') === 'pages');
  useEffect(() => {
    const open = () => setShowPageOrganizer(true);
    window.addEventListener('scrivar:organize-pages', open);
    return () => window.removeEventListener('scrivar:organize-pages', open);
  }, []);
  useEffect(() => { if (new URLSearchParams(location.search).get('panel') === 'pages') setShowPageOrganizer(true); }, [location.search]);

  // --- Form fields ---
  const {
    fields: formFields, values: formValues, setValue: setFormValue,
    clearAll: clearFormFields, highlightFields, setHighlightFields,
    hasFields: hasFormFields, fieldCount,
  } = useFormFields(pdfDoc);
  const [formProcessing, setFormProcessing] = useState(false);
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);

  // --- Find in document (Ctrl/Cmd+F) ---
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [findMatchCase, setFindMatchCase] = useState(false);
  const [findResults, setFindResults] = useState([]); // flat [{ pageNum, rect, id }]
  const [activeFindIndex, setActiveFindIndex] = useState(0);
  const [findSearching, setFindSearching] = useState(false);
  const documentSearchRef = useRef(null);

  // PDF.js DOM AnnotationLayer (Widget + FreeText). The canvas pass writes
  // per-annotation appearance into `annotationCanvasMap`; PdfAnnotationLayer
  // consumes the same map when assembling sections (so the yellow paint
  // appears inside the interactive DOM element when PDF.js provides one).
  // `canvasRenderToken` bumps each time PageCanvas finishes — PdfAnnotationLayer
  // depends on it so it re-renders against a freshly-populated map.
  //
  // For /FreeText placeholders (which PDF.js renders as empty sections),
  // PdfAnnotationLayer also injects our own yellow + transparent input
  // inside each `.freeTextAnnotation` section, wired to the same
  // formFields/formValues store so the existing save pipeline keeps
  // working (formValues → fieldData → drawText).
  const annotationCanvasMapRef = useRef(null);
  if (!annotationCanvasMapRef.current) annotationCanvasMapRef.current = new Map();
  const [canvasRenderToken, setCanvasRenderToken] = useState(0);
  const handleCanvasRenderComplete = useCallback(() => {
    setCanvasRenderToken((t) => t + 1);
  }, []);

  // --- Save/nav dialogs ---
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingNavAction, setPendingNavAction] = useState(null);
  const [flattenDialogOpen, setFlattenDialogOpen] = useState(false);

  // --- Signature ---
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [showSignaturePanel, setShowSignaturePanel] = useState(false);
  // When we open the panel from the "+ New signature" entry, force Create view
  // so the user doesn't have to tap through the list.
  const [signaturePanelView, setSignaturePanelView] = useState(null);
  // Lifted so both the Sign dropdown (in EditToolbar) and the Create modal
  // (SignaturePanel) see the same list — a sig saved from the modal must
  // appear in the dropdown without a remount / refetch. Viewer's in-page Sign
  // flow is full signatures only; the initials library is reached exclusively
  // via the eSign signer flow (SignDocument), which uses its own panel instance.
  const savedSigs = useSavedSignatures('signature');

  // --- Annotations ---
  const {
    annotations, activeTool, color, opacity, strokeWidth, fontSize: annotFontSize,
    showToolbar: showAnnotToolbar, canUndo, canRedo,
    setColor, setOpacity, setStrokeWidth, setFontSize: setAnnotFontSize,
    setShowToolbar: setShowAnnotToolbar, selectTool, clearTool,
    addAnnotation, deleteAnnotation, undo, redo,
    restoreAnnotations,
    selectedGroupId, selectGroup, deselectGroup,
    deleteGroup, replaceGroupAnnotations, commitAnnotationUpdate,
  } = useAnnotations();

  // --- Edits (text/image) ---
  const {
    edits, editMode, editTool, selectedEditIds, editStyles,
    canUndo: canUndoEdit, canRedo: canRedoEdit,
    setEditMode, setEditTool, addEdit, addEdits, updateEdit, updateEdits, commitUpdate, deleteEdit,
    deleteSelected: deleteSelectedEdit, selectEdit, toggleSelect: toggleSelectEdit,
    deselectAll: deselectAllEdits, updateStyle: updateEditStyle,
    undo: undoEdit, redo: redoEdit, clearEdits,
    clipboard: editClipboard, copySelected: copySelectedEdits,
    pasteEdits, duplicateSelected: duplicateSelectedEdits,
    updateSegmentStyle, restoreEdits,
  } = useEdits();
  const removalPreview = useContentRemovalPreview(pdfDoc, edits, selectedEditIds);
  const renderedPdfDoc = removalPreview.document;

  // Track saved state — snapshot of edit/annotation counts and per-item flag
  // states at the last save. `editFlagStates` / `annotFlagStates` let silent
  // cloud saves "bake" tombstone/modified flags so a subsequent tombstone on
  // a different item retriggers auto-save (without them, repeated tombstones
  // only fire one auto-save until the user adds/removes an edit).
  const savedStateRef = useRef({
    editCount: 0,
    annotCount: 0,
    editIds: new Set(),
    editFlagStates: new Map(),
    annotFlagStates: new Map(),
  });
  const meaningfulEdits = useMemo(() => edits.filter((edit) => !isUntouchedDetection(edit)), [edits]);
  const [inheritedDirty, setInheritedDirty] = useState(Boolean(location.state?.dirty));
  const [workingCopy, setWorkingCopy] = useState(Boolean(location.state?.workingCopy));
  const [, refreshSavedState] = useState(0);
  const savedNameRef = useRef(location.state?.file?.name);
  const saveInFlight = useRef(null);
  const latestSaveAction = useRef(null);
  const saveRevisionKey = JSON.stringify([meaningfulEdits.map(flagsOf), annotations.map(flagsOf), formValues, fileName]);
  const latestSaveState = useRef(null);
  latestSaveState.current = { file: location.state?.file, revision: saveRevisionKey };
  const viewerMounted = useRef(true);
  useEffect(() => { viewerMounted.current = true; return () => { viewerMounted.current = false; }; }, []);
  const currentEditIds = new Set(meaningfulEdits.map(e => e.id));
  const hasUnsavedChanges = (() => {
    if (inheritedDirty || (fileName && savedNameRef.current && fileName !== savedNameRef.current)) return true;
    const s = savedStateRef.current;
    if (formFields.some((field) => JSON.stringify(formValues[field.id]) !== JSON.stringify(s.formValues?.[field.id] ?? field.defaultValue ?? (field.type === 'checkbox' || field.type === 'radio' ? false : '')))) return true;
    if (meaningfulEdits.length !== s.editCount || annotations.length !== s.annotCount) return true;
    // Check if the actual edits changed (added/removed/modified)
    for (const e of meaningfulEdits) {
      if (!s.editIds.has(e.id)) return true;
        const now = flagsOf(e);
        const then = s.editFlagStates.get(e.id);
        if (now !== then) return true;
    }
    for (const id of s.editIds) {
      if (!currentEditIds.has(id)) return true;
    }
    // Tombstoned deletes and drag/style edits keep annotations.length constant
    // but still need a save — compare current flag state against the snapshot.
    for (const a of annotations) {
      const now = flagsOf(a);
      const then = s.annotFlagStates.get(annotKey(a));
      if (now !== then) return true;
    }
    return false;
  })();

  // ─── Cloud-aware viewer state ────────────────────────────────────────────
  // `docSource` drives the toolbar's source chip + the SaveMenu's primary
  // label. `cloudState` drives whether the "Save to cloud" menu row renders
  // active, locked (with upsell price), or signed-out.
  // `cloudUpsell` is the tier-aware CTA for the locked state (e.g. "Get Pro
  // Lifetime — $70" for a free user, "Add cloud — $4.99/mo" for a lifetime
  // owner). See lib/cloudUpsell.js for the canonical pricing table.
  const cloudState = getCloudState(user, account);
  const cloudUpsell = useMemo(() => getCloudUpsell(account), [account]);
  // Preserve the active destination even if cloud access expires. Save must
  // report that failure; the explicit Export a copy / Save As paths remain available.
  const docSource = cloudDocId
    ? 'cloud'
    : (location.state?.file ? 'device' : 'new');

  // --- Mutual exclusivity: annotation selection vs edit selection ---
  const selectedAnnotForToolbar = selectedGroupId ? annotations.find((a) => a.groupId === selectedGroupId) : null;
  const annotSelectable = editMode;

  // Native text selection is only useful when no annotation/edit tool is
  // actively placing something — otherwise a drag should draw/place, not
  // select text underneath. NOTE: `editMode` itself is NOT the right signal
  // here — the "Edit mode is always active" effect above (setEditMode(true)
  // right after mount, never set back to false) means editMode is true for
  // the entire life of the Viewer. Gating on it would make text permanently
  // unselectable. The actual per-drag capture happens in EditLayer, which
  // only claims pointer events for the placement tools 'text'/'date'/
  // 'signature' (see EditLayer.jsx's layerRef pointerEvents) — 'select'
  // (the default) and the other edit tools let clicks fall through. Mirror
  // that list here so text stays selectable except while actively placing.
  const isEditPlacementTool = editTool === 'text' || editTool === 'date' || editTool === 'signature';
  const textSelectable = !activeTool && !isEditPlacementTool;

  const handleSelectGroup = useCallback((groupId) => {
    selectGroup(groupId);
    deselectAllEdits();
  }, [selectGroup, deselectAllEdits]);

  const handleDeselectAll = useCallback(() => {
    deselectAllEdits();
    deselectGroup();
    setEditingSelection(null);
  }, [deselectAllEdits, deselectGroup]);

  /** Background click: deselect everything AND reset to select tool */
  const handleBackgroundClick = useCallback(() => {
    handleDeselectAll();
    if (editMode) {
      setEditTool('select');
      clearTool(); // clear annotation tool
    }
  }, [handleDeselectAll, editMode, setEditTool, clearTool]);

  const [editingSelection, setEditingSelection] = useState(null); // { editId, start, end, styles }

  // editingSelection can go stale: the user enters edit mode on box A (sets
  // editingSelection for A), then clicks box B to select it. The contenteditable
  // for A never blurs (mousedown is preventDefaulted on item wrappers, so focus
  // doesn't move), so editingSelection still points to A even though the toolbar
  // now reflects B. Gate it: only honor editingSelection when its editId is in
  // the current selectedEditIds set.
  const validEditingSelection = useMemo(() => {
    if (!editingSelection) return null;
    if (!selectedEditIds.has(editingSelection.editId)) return null;
    return editingSelection;
  }, [editingSelection, selectedEditIds]);

  // Toolbar display styles: prefer the active text-range selection's styles
  // (when the user has highlighted text inside a contenteditable), else fall
  // back to the single selected text edit's first-segment styles so the user
  // sees the actual current formatting of the box they clicked — not the
  // stale default in editStyles.
  const displaySelectionStyles = useMemo(() => {
    if (validEditingSelection?.styles) return validEditingSelection.styles;
    if (selectedEditIds.size === 1) {
      const [id] = selectedEditIds;
      const edit = edits.find((e) => e.id === id);
      if (edit?.type === 'text') {
        const seg = edit.segments?.[0]?.styles;
        if (seg) return seg;
      }
    }
    return null;
  }, [validEditingSelection, selectedEditIds, edits]);

  const handleUpdateSegmentStyle = useCallback((key, value) => {
    // Active text-range selection: apply to that range.
    if (validEditingSelection) {
      const { editId, start, end } = validEditingSelection;
      updateSegmentStyle(editId, key, value, start, end);
      return;
    }
    // No text-range selection but boxes are selected: apply to every selected
    // text box (whole-segment update). Without this, the toolbar's onChange
    // would silently no-op when the toolbar shows segment-derived display
    // styles for a selected-but-not-editing box.
    if (selectedEditIds.size > 0) {
      for (const id of selectedEditIds) {
        const edit = edits.find((e) => e.id === id);
        if (edit?.type === 'text') updateSegmentStyle(id, key, value);
      }
    }
  }, [validEditingSelection, selectedEditIds, edits, updateSegmentStyle]);

  const handleSegmentStyleChange = useCallback((key, value, start, end) => {
    if (!validEditingSelection) return;
    updateSegmentStyle(validEditingSelection.editId, key, value, start, end);
  }, [validEditingSelection, updateSegmentStyle]);

  const handleSelectEditWrapped = useCallback((id) => {
    selectEdit(id);
    deselectGroup();
    // Clear any stale text-range selection from a previously-edited box.
    setEditingSelection(null);
  }, [selectEdit, deselectGroup]);

  // When an annotation is selected, color/opacity/stroke changes update both brush + annotation
  const handleSetAnnotColor = useCallback((hex) => {
    setColor(hex);
    if (selectedGroupId) {
      replaceGroupAnnotations(selectedGroupId, (a) => ({ ...a, color: hex }));
      commitAnnotationUpdate();
    }
  }, [selectedGroupId, replaceGroupAnnotations, commitAnnotationUpdate, setColor]);

  const handleSetAnnotOpacity = useCallback((val) => {
    setOpacity(val);
    if (selectedGroupId) {
      replaceGroupAnnotations(selectedGroupId, (a) => ({ ...a, opacity: val }));
      commitAnnotationUpdate();
    }
  }, [selectedGroupId, replaceGroupAnnotations, commitAnnotationUpdate, setOpacity]);

  const handleSetAnnotStrokeWidth = useCallback((val) => {
    setStrokeWidth(val);
    if (selectedGroupId) {
      replaceGroupAnnotations(selectedGroupId, (a) => ({ ...a, strokeWidth: val }));
      commitAnnotationUpdate();
    }
  }, [selectedGroupId, replaceGroupAnnotations, commitAnnotationUpdate, setStrokeWidth]);

  // --- Content detection (Edit Text) ---
  // detectedPagesRef/candidatesByPage/detectionRan are declared further down,
  // right next to the per-page detection effects that own them.
  const [detecting, setDetecting] = useState(false);
  const [showDetectingOverlay, setShowDetectingOverlay] = useState(false);
  const showTimerRef = useRef(null);
  const [noTextFound, setNoTextFound] = useState(false);
  const fileArrayBufferRef = useRef(null); // Clone of file bytes for image detection
  const imageDetectionDoneRef = useRef(false); // Tracks if images have been enriched
  const [imageDetection, setImageDetection] = useState(null);
  // Latches true the first time the user selects the image tool (EditToolbar's
  // "Add Image" button, which sets editTool to 'image' before opening the file
  // picker). This — NOT merely opening Edit Text — is the sanctioned trigger
  // for the whole-document image sweep below: opening Edit Text on an
  // image-bearing PDF must dirty nothing, but choosing the image tool is a
  // deliberate user action and may.
  const imageToolUsedRef = useRef(false);

  // --- Desktop title bar ---
  useEffect(() => {
    updateTitle(fileName || null, hasUnsavedChanges);
    return () => updateTitle(null); // reset on unmount
  }, [fileName, hasUnsavedChanges]);

  // Edit mode is always active — tools are always available
  useEffect(() => {
    if (!editMode) {
      setEditMode(true);
      setEditTool('select');
      setShowAnnotToolbar(true);
    }
  }, [editMode, setEditMode, setEditTool, setShowAnnotToolbar]);

  // Signature placement hint toast
  const [signaturePlacementHint, setSignaturePlacementHint] = useState(false);

  // Load file from router state
  useEffect(() => {
    const file = location.state?.file;
    if (file) {
      setInheritedDirty(Boolean(location.state?.dirty));
      // Store a copy of the ArrayBuffer for background image detection
      file.arrayBuffer().then((ab) => {
        fileArrayBufferRef.current = ab.slice(0);
      });
      loadFile(file);
      // Track recent file in the command palette / web localStorage.
      // No-op on desktop — the main process already records its own list.
      // `recordRecent` is currently synchronous (returns undefined). It used
      // to return a promise (or could again if the storage backend goes
      // async), so guard the .catch via optional-chain BEFORE the property
      // access. The previous form — `recordRecent(...).catch?.(() => {})` —
      // read `.catch` on undefined and crashed with "Cannot read properties
      // of undefined (reading 'catch')", blanking the entire /viewer page.
      // `?.catch(...)` short-circuits to undefined when the call returns
      // non-thenable, and still works if it ever returns a real promise.
      recordRecent({ name: file.name, size: file.size })?.catch(() => {});
      // Track original file path for direct save (Electron)
      setCurrentFilePath(location.state?.filePath || null);
      setWorkingCopy(Boolean(location.state?.workingCopy));
      savedNameRef.current = file.name;
      setCloudDocId(location.state?.cloudDocId || null);
    } else {
      navigate('/', { replace: true });
    }
  }, [location.state?.file, loadFile]);

  useEffect(() => {
    const mode = new URLSearchParams(location.search).get('mode');
    if (mode === 'sign') setEditTool('signature');
    if (mode === 'editText') setEditTool('editText');
  }, [location.search, setEditTool]);

  // After an AI execute that swapped the document, show the Undo toast.
  // We guard with a ref keyed on the aiUndoFile object reference: each fresh
  // AI execute produces a new File, so a new reference unlocks the toast.
  // Returning to the same aiUndoFile (e.g., via browser back/forward) won't
  // re-show. We deliberately avoid the alternative of stripping AI keys via
  // a replace-navigate — that mutates `location.state`, which the pre-
  // existing file-load effect treats as a new mount and re-calls loadFile.
  const undoShownRef = useRef(null);
  useEffect(() => {
    const aiUndoFile = location.state?.aiUndoFile;
    const aiUndoSummary = location.state?.aiUndoSummary;
    if (!aiUndoFile || !aiUndoSummary) return;
    if (undoShownRef.current === aiUndoFile) return;
    undoShownRef.current = aiUndoFile;
    const { key, args } = aiUndoSummary;
    showToast({
      message: t(key, args),
      action: {
        label: t('ai.undo_button'),
        onClick: () => navigate('/viewer', { state: { file: aiUndoFile } }),
      },
      durationMs: 6000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.aiUndoFile]);

  // --- Faithful open + re-editable reopen ---
  //
  // THIRD-PARTY files (no Scrivar sidecar): we auto-restore NOTHING. PDF.js
  // paints every page + annotation faithfully onto the canvas via the stored
  // /AP appearance streams, so the document opens as its single source of
  // truth — identical to SmallPDF / Preview / Chrome. (`usePDF` only strips
  // when a sidecar is present, so here it renders the original bytes.)
  //
  // OUR OWN files (Scrivar sidecar present): usePDF renders the canvas from a
  // copy with our /NM scrivar: annots STRIPPED, then exposes the sidecar.
  // Below we restore the sidecar's edits/annotations into the editable layer.
  // Net: canvas shows page + third-party annots (faithful); EditLayer shows
  // OUR content (selectable/editable); each piece of our content lives in
  // exactly ONE place — no double render. We do NOT force edit mode: the
  // EditLayer renders the restored items statically at their saved positions,
  // and the user enters Edit to modify them.
  useEffect(() => {
    if (!sidecar) return;
    if (sidecar.edits?.length) restoreEdits(sidecar.edits);
    if (sidecar.annotations?.length) restoreAnnotations(sidecar.annotations);
    // Treat the restored state as the saved baseline so unsaved-change
    // tracking doesn't immediately flag the freshly-opened doc as dirty.
    savedStateRef.current = {
      editCount: sidecar.edits?.length || 0,
      annotCount: sidecar.annotations?.length || 0,
      editIds: new Set((sidecar.edits || []).map((e) => e.id)),
      editFlagStates: new Map((sidecar.edits || []).map((e) => [e.id, flagsOf(e)])),
      annotFlagStates: new Map((sidecar.annotations || []).map((a) => [annotKey(a), flagsOf(a)])),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidecar]);

  // --- Per-page text detection → lightweight CANDIDATES (Task 7) ---
  //
  // Candidates are NOT edits. They're lightweight hit-targets rendered by
  // EditLayer that promote to a real edit (via addEdit) only when the user
  // clicks into or deletes one. This keeps "enter Edit Text" instant (one
  // page, not the whole document) and keeps hasUnsavedChanges honest — no
  // edits exist, so nothing is dirty, until the user actually acts.
  //
  // detectedPagesRef caches each page's raw detector output (ref, not state
  // — detection results don't need to trigger renders on their own; the
  // candidatesByPage state update that follows does). Cleared on file change.
  const detectedPagesRef = useRef(new Map()); // pageNum -> { textBlocks, ran: true }
  const detectingPagesRef = useRef(new Set()); // in-flight guard against duplicate detection
  const [candidatesByPage, setCandidatesByPage] = useState(new Map()); // pageNum -> block[]
  const [detectionRan, setDetectionRan] = useState(false); // true once ANY page has been detected

  // Bumped every time a new file loads (see the reset effect below).
  // detectPageIfNeeded closes over `pdfDoc` at call time, but its awaits can
  // resolve AFTER a file swap has already reset detectedPagesRef/
  // candidatesByPage for the NEW file — without this guard, a late resolve
  // from the OLD file's in-flight getPage()/detectTextContent() would write
  // stale blocks into the new file's caches (wrong content silently appears
  // as "detected" on the new document, and that page's slot in
  // detectingPagesRef never gets cleared for the new generation). Every
  // write below re-checks the captured generation against the current one
  // and bails if a file swap happened mid-flight.
  const detectionGenRef = useRef(0);

  /** Detect a single page's text content and seed its candidates. No-ops if already detected/in-flight. */
  const detectPageIfNeeded = useCallback(async (pageNum) => {
    if (!pdfDoc || pageNum < 1 || pageNum > pdfDoc.numPages) return;
    if (detectedPagesRef.current.has(pageNum) || detectingPagesRef.current.has(pageNum)) return;
    const gen = detectionGenRef.current;
    detectingPagesRef.current.add(pageNum);
    try {
      const page = await pdfDoc.getPage(pageNum);
      if (detectionGenRef.current !== gen) return; // file swapped mid-flight — discard
      const { textBlocks } = await detectTextContent(page, pageNum, geometryForPage(pageNum));
      if (detectionGenRef.current !== gen) return; // file swapped mid-flight — discard
      detectedPagesRef.current.set(pageNum, { textBlocks, ran: true });
      setCandidatesByPage((prev) => {
        const next = new Map(prev);
        next.set(pageNum, textBlocks);
        return next;
      });
      setDetectionRan(true);
    } finally {
      // Only clean up THIS generation's in-flight guard — a stale resolve
      // must not delete the new generation's legitimately-in-flight entry
      // for the same page number.
      if (detectionGenRef.current === gen) {
        detectingPagesRef.current.delete(pageNum);
      }
    }
  }, [pdfDoc, geometryForPage]);

  // Entering Edit Text: detect the current visible page synchronously (fast —
  // one page), show the brief "Analyzing document..." overlay only if that
  // takes >300ms, then flip the tool back to 'select' so the click/promote
  // interactions on EditLayer take over.
  useEffect(() => {
    if (editTool !== 'editText' || !pdfDoc) return;
    let cancelled = false;

    (async () => {
      setDetecting(true);
      setNoTextFound(false);
      showTimerRef.current = setTimeout(() => setShowDetectingOverlay(true), 300);

      try {
        await detectPageIfNeeded(currentPage);
        if (cancelled) return;

        const result = detectedPagesRef.current.get(currentPage);
        if (result && result.textBlocks.length === 0) {
          setNoTextFound(true);
          setTimeout(() => setNoTextFound(false), 5000);
        }
      } catch (err) {
        // A rejected getPage()/detectTextContent() (e.g. corrupted page,
        // cancelled render) must not wedge the overlay/tool state below —
        // without this catch, everything after the await simply never runs
        // and the "Analyzing document..." overlay + editText tool stay
        // stuck forever.
        if (import.meta.env.DEV) console.warn('Edit Text page detection failed:', err);
      } finally {
        if (!cancelled) {
          clearTimeout(showTimerRef.current);
          setShowDetectingOverlay(false);
          setDetecting(false);
          setEditTool('select');
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(showTimerRef.current);
    };
  }, [editTool, pdfDoc, currentPage, detectPageIfNeeded, setEditTool]);

  // While in edit mode, detect newly-visible pages lazily as the user
  // scrolls/paginates — currentPage tracks both ScrollablePages' "most
  // visible" reporting (via handleVisiblePageChange) and large-file pagination.
  //
  // Also detect currentPage ± 1 (clamped to 1..numPages): ScrollablePages
  // renders a ±1 page buffer around the visible range (RENDER_BUFFER in
  // ScrollablePages.jsx) — every page that has an actual canvas on screen
  // should have candidates ready, not just the "most visible" one. The
  // per-page cache + in-flight guards inside detectPageIfNeeded make the
  // extra two calls cheap no-ops once each page has been detected once.
  useEffect(() => {
    if (!editMode || !pdfDoc) return;
    if (detectedPagesRef.current.size === 0) return; // Edit Text hasn't been opened yet this session
    // Fire-and-forget: detectPageIfNeeded already clears its own in-flight
    // guard in a finally, but a rejection here would otherwise be an
    // unhandled promise rejection with no caller to see it.
    const pagesToDetect = [currentPage - 1, currentPage, currentPage + 1]
      .filter((p) => p >= 1 && p <= pdfDoc.numPages);
    for (const pageNum of pagesToDetect) {
      detectPageIfNeeded(pageNum).catch((err) => {
        if (import.meta.env.DEV) console.warn('Lazy page detection failed:', err);
      });
    }
  }, [editMode, pdfDoc, currentPage, detectPageIfNeeded]);

  /**
   * Promote a candidate block to a real edit. intent:'edit' builds the edit,
   * selects it, and marks it editing=true so EditItem opens straight into
   * the contenteditable (mirrors the click-to-place text/date tool paths in
   * EditLayer, which already set editing:true on the object handed to
   * addEdit). intent:'delete' builds the edit then immediately tombstones it
   * via deleteEdit — deleteEdit already knows to keep it as a tombstone
   * (originalBounds present, always true for a detected block) rather than
   * dropping it, so it whites out on save per Task 3's semantics. This relies
   * on useEdits' pushHistory syncing editsRef.current immediately (not just
   * on the next render) — addEdit followed synchronously by deleteEdit in
   * the same handler would otherwise have deleteEdit read a stale ref that
   * doesn't yet contain the edit addEdit just pushed, silently discarding it
   * instead of tombstoning it (caught by a regression test in
   * useEdits.test.js during this task's browser verification).
   *
   * candidatesByPage is NEVER mutated here (Bug B fix — "block unselectable
   * after promote→delete→undo"). The old code permanently removed the
   * promoted block from candidatesByPage, but undo only restores useEdits'
   * edit-array snapshots — it knows nothing about candidatesByPage — so
   * undoing PAST a promotion left the block with neither a live edit nor a
   * candidate: invisible to every click handler until the file reloaded.
   * `blockToEdit` stamps `sourceBlockId: block.id` on the edit, and
   * `visibleCandidates` (lib/detectedEditFactory.js) derives, at render time,
   * which candidates are hidden because a live edit currently claims them.
   * Deleting/undoing the edit makes the candidate reappear automatically —
   * no separate "restore" bookkeeping needed.
   */
  const handlePromoteCandidate = useCallback((pageNum, block, intent) => {
    const edit = blockToEdit(block);
    if (intent === 'delete') {
      const id = addEdit(edit);
      deleteEdit(id);
    } else {
      const id = addEdit({ ...edit, editing: true });
      handleSelectEditWrapped(id);
    }
  }, [addEdit, deleteEdit, handleSelectEditWrapped]);

  // --- Phase 2: Image detection (lazy background, separate document) ---
  //
  // Design call: images stay a WHOLE-DOCUMENT pass (rather than becoming
  // per-page lazy + candidate-based like text) — images already ran on a
  // SEPARATE pdf.js document in the background and never blocked entering
  // Edit Text. Making images candidates too would roughly double this diff
  // for a problem that isn't the one this task is fixing.
  //
  // Trigger: FIRST IMAGE-TOOL USE, not `detectionRan`. Gating on
  // `detectionRan` (which flips true on mere Edit Text entry, since text
  // detection runs synchronously for the visible page) meant opening Edit
  // Text on ANY image-bearing PDF silently ran this sweep and called
  // `addEdits(imageEdits)` — which flips `hasUnsavedChanges` — with zero
  // user action. Selecting the image tool (EditToolbar's "Add Image"
  // button, which now sets editTool to 'image' before opening the file
  // picker) is a genuine user action and is the sanctioned trigger instead.
  // `imageToolUsedRef` latches true the first time that happens and keeps
  // the sweep armed even after editTool moves on (e.g. back to 'select'
  // once the picked image lands); `imageDetectionDoneRef` still ensures the
  // sweep itself only ever runs once per document.
  useEffect(() => {
    if (editTool === 'image') imageToolUsedRef.current = true;
  }, [editTool]);

  useEffect(() => {
    const shouldRun = shouldRunImageSweep({
      imageToolUsed: imageToolUsedRef.current,
      alreadyDone: imageDetectionDoneRef.current,
      hasFileBytes: fileArrayBufferRef.current,
    });
    if (!shouldRun) return;
    let cancelled = false;
    setImageDetection({ busy: true });

    (async () => {
      let imageDoc;
      try {
        // Create a separate PDF.js document to avoid RenderingCancelledException
        imageDoc = await pdfjsLib.getDocument({ data: fileArrayBufferRef.current.slice(0) }).promise;
        const numPg = imageDoc.numPages;
        const imageEdits = [];

        for (let i = 1; i <= numPg; i++) {
          if (cancelled) break;
          const page = await imageDoc.getPage(i);
          const { images } = await detectImageContent(page, i, geometryForPage(i));

          for (const img of images) {
            imageEdits.push({
              type: 'image',
              source: 'detected',
              pageNum: img.pageNum,
              x: img.x,
              y: img.y,
              width: img.width,
              height: img.height,
              content: img.dataUrl,
              originalBounds: img.originalBounds,
            });
          }

          // Yield between pages so the renderer stays responsive
          await new Promise((r) => setTimeout(r, 0));
        }

        if (!cancelled && imageEdits.length > 0) {
          addEdits(imageEdits);
        }

        if (!cancelled) setImageDetection({ count: imageEdits.length });
      } catch (err) {
        if (!cancelled) setImageDetection({ error: `Could not find images: ${err.message}` });
      } finally { imageDoc?.destroy(); }

      if (!cancelled) {
        imageDetectionDoneRef.current = true;
      }
    })();

    return () => { cancelled = true; };
    // editTool is the dep that re-evaluates this effect at the moment the
    // ref above flips true (refs alone don't trigger re-runs); the effect
    // body itself reads the ref, not editTool, so subsequent tool changes
    // don't disarm it once latched.
  }, [editTool, addEdits, geometryForPage]);

  // Reset detected content and clear detected edits/candidates when a new file is loaded
  useEffect(() => {
    // Invalidate any in-flight detectPageIfNeeded calls from the PREVIOUS
    // file — see detectionGenRef's declaration comment above.
    detectionGenRef.current += 1;
    detectedPagesRef.current = new Map();
    detectingPagesRef.current = new Set();
    setCandidatesByPage(new Map());
    setDetectionRan(false);
    imageDetectionDoneRef.current = false;
    setImageDetection(null);
    if (imageToolUsedRef.current) setEditTool('select');
    imageToolUsedRef.current = false;
    clearEdits();
    restoreAnnotations([]);
    savedStateRef.current = { editCount: 0, annotCount: 0, editIds: new Set(), editFlagStates: new Map(), annotFlagStates: new Map() };
  }, [location.state?.file, clearEdits, restoreAnnotations, setEditTool]);

  // --- Find in document ---
  // Per-page map of deleted (tombstoned) detected-block regions, so find
  // results landing mid-stream can be filtered against whatever's deleted
  // *right now* — search indexes the loaded document bytes directly and has
  // no idea an edit tombstoned a block the user can no longer see (see
  // filterMatchesByDeletedRegions' doc comment in documentSearch.js).
  const deletedRegionsByPage = useMemo(() => buildDeletedRegionsByPage(edits), [edits]);
  const deletedRegionsByPageRef = useRef(deletedRegionsByPage);
  deletedRegionsByPageRef.current = deletedRegionsByPage;

  // Live re-filter: the streaming-search effect below only filters results
  // as they land page-by-page. Deleting a block while the find bar is
  // already open (and fully populated) left its stale matches counted and
  // highlighted until the next search. Re-run the filter over the
  // already-accumulated findResults whenever deletedRegionsByPage changes.
  // refilterIfShrunk returns null when nothing would be dropped, so the
  // setState (and this effect's own re-run via the findResults dependency)
  // is skipped — that's what keeps this from looping. Drop-only: undelete/
  // undo restores any dropped match via the next re-search, not here.
  useEffect(() => {
    if (!findOpen || findResults.length === 0) return;
    const next = refilterIfShrunk(findResults, deletedRegionsByPage);
    if (next) setFindResults(next);
  }, [deletedRegionsByPage, findOpen, findResults]);

  // A fresh createDocumentSearch per pdfDoc — its per-page text cache is
  // keyed to a single document, so a new file needs a new instance. Also
  // closes the bar and clears any stale results from the previous document.
  useEffect(() => {
    documentSearchRef.current?.cancel();
    documentSearchRef.current = pdfDoc ? createDocumentSearch(pdfDoc, { getPageGeometry: (pageNum) => searchGeometryRef.current(pageNum) }) : null;
    setFindOpen(false);
    setFindQuery('');
    setFindResults([]);
    setActiveFindIndex(0);
    setFindSearching(false);
  }, [pdfDoc]);

  // Debounced streaming search: cancel any in-flight generator, then start a
  // fresh one 200ms after the query/matchCase settle. Appends results
  // page-by-page as the generator yields, so the counter/highlights update
  // progressively on large documents instead of waiting for the whole scan.
  useEffect(() => {
    const engine = documentSearchRef.current;
    if (!engine || !findOpen || findQuery.trim().length < 2) {
      setFindResults([]);
      setActiveFindIndex(0);
      setFindSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      engine.cancel();
      setFindResults([]);
      setActiveFindIndex(0);
      setFindSearching(true);
      const gen = engine.search(findQuery, { matchCase: findMatchCase, edits });
      let matchSeq = 0;
      try {
        for await (const pageResult of gen) {
          if (cancelled) return;
          const flat = pageResult.matches.map((m) => ({
            pageNum: pageResult.pageNum,
            rect: m.rect,
            id: `${pageResult.pageNum}-${matchSeq++}`,
          }));
          // Drop matches inside a pending deletion so the FindBar counter and
          // highlights never point at text the user just deleted.
          const visible = filterMatchesByDeletedRegions(flat, deletedRegionsByPageRef.current);
          setFindResults((prev) => [...prev, ...visible]);
        }
      } finally {
        if (!cancelled) setFindSearching(false);
      }
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      engine.cancel();
    };
  }, [findQuery, findMatchCase, findOpen, edits]);

  // Navigate to the active match — jump the page ONLY via goToPage. The
  // scroll-sync effect below (keyed on currentPage) performs the actual
  // scroll in scroll mode, and the large-file branch swaps pages off
  // currentPage directly. Calling scrollToPage here too would double-drive
  // the scroll position.
  useEffect(() => {
    if (!findOpen || findResults.length === 0) return;
    const match = findResults[activeFindIndex];
    if (match) goToPage(match.pageNum);
  }, [findOpen, findResults, activeFindIndex, goToPage]);

  // Clamp the active index whenever the result set shrinks (e.g. a query
  // edit reset it to a shorter list mid-stream).
  useEffect(() => {
    if (activeFindIndex >= findResults.length && findResults.length > 0) {
      setActiveFindIndex(0);
    }
  }, [findResults.length, activeFindIndex]);

  const handleFindClose = useCallback(() => {
    setFindOpen(false);
    documentSearchRef.current?.cancel();
    setFindResults([]);
    setActiveFindIndex(0);
    setFindSearching(false);
  }, []);

  const handleFindOpen = useCallback(() => {
    setFindOpen(true);
  }, []);

  const renderSearchHighlightOverlay = useCallback(
    (pageNum) => {
      if (!findOpen || findResults.length === 0) return null;
      const activeMatchId = findResults[activeFindIndex]?.id;
      return (
        <SearchHighlightLayer
          geometry={geometryForPage(pageNum)}
          matches={findResults}
          activeMatchId={activeMatchId}
          pageNum={pageNum}
          zoom={zoom}
        />
      );
    },
    [findOpen, findResults, activeFindIndex, zoom, geometryForPage]
  );

  // --- Scroll sync ---
  const handleVisiblePageChange = useCallback((page) => {
    scrollTriggeredRef.current = true;
    goToPage(page);
  }, [goToPage]);

  useEffect(() => {
    if (scrollTriggeredRef.current) {
      scrollTriggeredRef.current = false;
      return;
    }
    if (!isLargeFile && scrollPagesRef.current) {
      scrollPagesRef.current.scrollToPage(currentPage);
    }
  }, [currentPage, isLargeFile]);

  const handleFitToWidth = useCallback(() => {
    let containerWidth;
    if (!isLargeFile && scrollPagesRef.current?.container) {
      containerWidth = scrollPagesRef.current.container.clientWidth - 64;
    } else if (singlePageContainerRef.current) {
      containerWidth = singlePageContainerRef.current.clientWidth - 64;
    }
    if (containerWidth) fitToWidth(containerWidth);
  }, [fitToWidth, isLargeFile]);

  // Auto-fit to width on initial file load — scales down wide pages but never zooms in beyond 100%
  useEffect(() => {
    if (!pdfDoc || pdfDoc === autoFittedDocRef.current) return;
    const raf = requestAnimationFrame(() => {
      let containerWidth;
      if (!isLargeFile && scrollPagesRef.current?.container) {
        containerWidth = scrollPagesRef.current.container.clientWidth - 64;
      } else if (singlePageContainerRef.current) {
        containerWidth = singlePageContainerRef.current.clientWidth - 64;
      }
      if (containerWidth && containerWidth > 0) {
        const naturalScale = containerWidth / maxPageWidth;
        if (naturalScale < 1.0) {
          // Widest page overflows container — scale down to fit
          fitToWidth(containerWidth);
        }
        // Otherwise leave at 1.0 (default) — don't zoom in small pages
      }
      autoFittedDocRef.current = pdfDoc;
    });
    return () => cancelAnimationFrame(raf);
  }, [pdfDoc, isLargeFile, maxPageWidth, fitToWidth]);

  // Re-fit on window resize when fitted mode is active
  useEffect(() => {
    if (!isFitted || !pdfDoc) return;
    const handleResize = () => handleFitToWidth();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFitted, pdfDoc, handleFitToWidth]);

  // --- Cross-page drag (owned by Viewer) --- must be before keyboard shortcuts
  // The ref is the live drag (owned by handleDragStart/endDrag, set
  // synchronously); the state mirrors it only so renders can see a drag.
  const [viewerDragState, setViewerDragState] = useState(null);
  const dragStateRef = useRef(null);
  const dragTrackerRef = useRef(null);

  const endDrag = useCallback((reason) => {
    const ds = dragStateRef.current;
    if (!ds) return;
    dragStateRef.current = null;
    dragTrackerRef.current?.stop();
    dragTrackerRef.current = null;
    commitUpdate();
    setViewerDragState(null);
    if (import.meta.env.DEV) {
      console.debug(`[EditDrag] ended: ${reason}`, { editId: ds.editId, page: ds.currentPage });
    }
  }, [commitUpdate]);

  // --- Keyboard shortcuts ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      const isTextInput = tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || e.target.isContentEditable;
      // Allow Ctrl+B/I/U/Shift+X through even in text inputs for formatting
      const isFormatShortcut = (e.ctrlKey || e.metaKey) && editMode && selectedEditIds.size > 0 &&
        (e.key === 'b' || e.key === 'i' || e.key === 'u' || (e.key === 'x' && e.shiftKey));
      if (isTextInput && !isFormatShortcut) return;

      // Undo/Redo — when an annotation tool is active, prefer annotation undo;
      // otherwise prefer edit undo; fall back to whichever has history.
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (activeTool && canUndo) undo();
        else if (canUndoEdit) undoEdit();
        else if (canUndo) undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'Z' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (activeTool && canRedo) redo();
        else if (canRedoEdit) redoEdit();
        else if (canRedo) redo();
        return;
      }

      // Copy/Paste/Duplicate/Bold/Italic/Underline (edit mode only)
      if (editMode && (e.ctrlKey || e.metaKey)) {
        const isEditingText = document.activeElement?.isContentEditable;
        if (e.key === 'c' && !isEditingText) {
          // A user with an active text selection and no edit selected wants
          // native copy (document text), not the edit-clipboard copy.
          if (!window.getSelection()?.isCollapsed && selectedEditIds.size === 0) return;
          e.preventDefault(); copySelectedEdits(); return;
        }
        if (e.key === 'v' && !isEditingText) {
          e.preventDefault(); pasteEdits(currentPage, pageSize.width, pageSize.height); return;
        }
        if (e.key === 'd') {
          e.preventDefault(); duplicateSelectedEdits(pageSize.width, pageSize.height); return;
        }
        // Bold / Italic / Underline for selected text edits
        if (e.key === 'b' && selectedEditIds.size > 0) {
          e.preventDefault(); updateEditStyle('bold', !editStyles.bold); return;
        }
        if (e.key === 'i' && selectedEditIds.size > 0) {
          e.preventDefault(); updateEditStyle('italic', !editStyles.italic); return;
        }
        if (e.key === 'u' && selectedEditIds.size > 0) {
          e.preventDefault(); updateEditStyle('underline', !editStyles.underline); return;
        }
        if (e.key === 'x' && e.shiftKey && selectedEditIds.size > 0) {
          e.preventDefault(); updateEditStyle('strikethrough', !editStyles.strikethrough); return;
        }
      }

      // Delete selected annotation
      // Don't intercept Delete/Backspace when user is actively typing in a contenteditable
      const isTypingInEdit = document.activeElement?.isContentEditable;

      if ((e.key === 'Delete' || e.key === 'Backspace') && editMode && selectedGroupId && !isTypingInEdit) {
        e.preventDefault(); deleteGroup(selectedGroupId); return;
      }

      // Delete selected edits
      if ((e.key === 'Delete' || e.key === 'Backspace') && editMode && selectedEditIds.size > 0 && !isTypingInEdit) {
        e.preventDefault(); deleteSelectedEdit(); return;
      }

      // Escape
      if (e.key === 'Escape') {
        if (viewerDragState) { endDrag('escape'); return; }
        if (activeTool) { clearTool(); return; }
        if (selectedGroupId) { deselectGroup(); return; }
        if (selectedEditIds.size > 0) { deselectAllEdits(); return; }
      }

      // Zoom
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=' || e.key === '-')) {
        e.preventDefault();
        if (e.key === '-') zoomOut(); else zoomIn();
        return;
      }

      // Page nav (large files)
      if (isLargeFile) {
        switch (e.key) {
          case 'ArrowRight': case 'ArrowDown': e.preventDefault(); nextPage(); break;
          case 'ArrowLeft': case 'ArrowUp': e.preventDefault(); prevPage(); break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLargeFile, nextPage, prevPage, zoomIn, zoomOut, undo, redo, undoEdit, redoEdit, activeTool, clearTool, editMode, selectedEditIds, deleteSelectedEdit, deselectAllEdits, copySelectedEdits, pasteEdits, duplicateSelectedEdits, currentPage, pageSize, viewerDragState, endDrag, updateEditStyle, editStyles, canUndo, canRedo, canUndoEdit, canRedoEdit, selectedGroupId, deselectGroup, deleteGroup]);

  // Unsaved-changes signal for both web + desktop close paths.
  //   Web:     browser fires beforeunload — preventDefault() shows the
  //            native "Changes may not be saved" prompt.
  //   Desktop: main.js intercepts the window 'close' event and reads
  //            window.__scrivarHasUnsavedChanges to decide whether to
  //            show a native Save/Don't Save/Cancel dialog. We do NOT
  //            preventDefault on beforeunload in Electron — it silently
  //            blocks the close with no prompt (Electron quirk: ignores
  //            the browser-style confirm), which leaves the app
  //            unresponsive to the red close button. Bit users in v1.0.12.
  useEffect(() => {
    const hasUnsaved = hasUnsavedChanges;
    const isDesktopApp = !!window.electronAPI;

    // Always keep the global in sync so main.js can read it at close-time.
    window.__scrivarHasUnsavedChanges = hasUnsaved;

    // Web only: use the browser's native unload confirmation.
    if (!isDesktopApp) {
      const handler = (e) => {
        if (hasUnsaved) {
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', handler);
      return () => {
        window.removeEventListener('beforeunload', handler);
        window.__scrivarHasUnsavedChanges = false;
      };
    }

    return () => {
      window.__scrivarHasUnsavedChanges = false;
    };
  }, [hasUnsavedChanges]);

  // --- Unified save: applies form fills + edits + annotations together ---
  const buildFieldData = useCallback(
    () => toFieldData(formFields, formValues),
    [formFields, formValues]
  );

  // --- Toast (non-blocking confirmation for save/save-as) ---
  // Toast supports two shapes:
  //   - string  → plain message, auto-dismisses
  //   - object  → { message, action: { label, onClick } | null, durationMs? }
  // The action button is rendered when present; clicking it dismisses the toast.
  const [toastMessage, setToastMessage] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    const durationMs = (msg && typeof msg === 'object' && msg.durationMs) || 1500;
    toastTimerRef.current = setTimeout(() => setToastMessage(null), durationMs);
  }, []);
  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(null);
  }, []);
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  // Canonical save shape — what Save/Save As/Download/Print use. Flatten is a
  // different byte shape (sidecar stripped, annotations baked) and bypasses
  // this cache below.
  const canonicalOptions = useMemo(
    () => ({ flatten: false, preserveOverlays: true }),
    []
  );

  // Debounced background pre-compute so Download clicks resolve instantly.
  // The compute callback itself assembles the full options (adds fieldData
  // from the current form values) and delegates to saveViaWorker.
  //
  // fieldData MUST include `source`, `pageNum`, and `rect` for the save
  // pipeline to route /FreeText placeholders through the drawText branch.
  // saveAllChanges filters by `source` — without it, every field falls
  // through to the widget-AcroForm branch and FreeText values silently
  // never get written. Pre-existing bug surfaced after migrating
  // /FreeText to PdfAnnotationLayer (which puts more pressure on this
  // path now that FormFieldOverlay isn't doing the parallel write).
  // Shared with the direct save via toFieldData so the two can't drift again.
  const precomputeCompute = useCallback(
    async ({ arrayBuffer, edits, annotations, formValues, options }) => {
      const fieldData = hasFormFields ? toFieldData(formFields, formValues) : undefined;
      return saveViaWorker({
        arrayBuffer,
        edits,
        annotations,
        options: { ...options, fieldData },
      });
    },
    [formFields, hasFormFields]
  );

  const { getSavedBytes: getPrecomputedBytes } = usePrecomputedSave({
    file: location.state?.file,
    edits,
    annotations,
    formValues,
    options: canonicalOptions,
    compute: precomputeCompute,
  });

  // Pure byte generator — produces canonical PDF bytes with editable appearances.
  // Save, Save As, Download, and Print all call this and then do their
  // platform-specific thing. Kept side-effect-free so Print doesn't clear
  // the editor state.
  const generateSavedBytes = useCallback(async (options = {}) => {
    const file = location.state?.file;
    if (!file) return null;

    // Flatten produces different bytes (sidecar stripped, annots baked) —
    // bypass the precompute cache and hit the worker directly.
    if (options.flatten) {
      const arrayBuffer = await file.arrayBuffer();
      const fieldData = hasFormFields ? buildFieldData() : undefined;
      const bytes = await saveViaWorker({
        arrayBuffer,
        edits: edits.length > 0 ? edits : [],
        annotations: annotations.length > 0 ? annotations : [],
        options: { fieldData, flatten: true, preserveOverlays: false },
      });
      return new Uint8Array(bytes);
    }

    const bytes = await getPrecomputedBytes();
    return new Uint8Array(bytes);
  }, [location.state, hasFormFields, buildFieldData, annotations, edits, getPrecomputedBytes]);

  // Register without remounting the editor when a toolbar or navigation item
  // changes. Export is read from the latest render, including the last keypress.
  const exportWorkingCopyRef = useRef(null);
  exportWorkingCopyRef.current = async () => {
    const original = location.state?.file;
    if (!original) return null;
    const file = hasUnsavedChanges || meaningfulEdits.length || annotations.length || hasFormFields || fileName !== original.name
      ? new File([await generateSavedBytes()], fileName, { type: 'application/pdf' })
      : original;
    return { file, filePath: currentFilePath, cloudDocId, dirty: hasUnsavedChanges, workingCopy, protectOriginal: hasDestructiveTextEdits(edits) };
  };
  const registerExporter = workspace?.registerExporter;
  useEffect(() => registerExporter?.(() => exportWorkingCopyRef.current()), [registerExporter]);

  // Save a captured revision without reloading the viewer or destroying Undo.
  // The next save is regenerated from the session's original base + live edits.
  const saveRevision = useCallback((intent = 'save', { silent = false } = {}) => {
    if (saveInFlight.current) {
      if (saveInFlight.current.intent === intent) return saveInFlight.current.promise;
      return saveInFlight.current.promise.catch(() => false).then(() => latestSaveAction.current(intent, { silent }));
    }
    if (!location.state?.file) return Promise.resolve(false);
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return Promise.resolve(false);
    }
    commitUpdate();
    const baseline = {
      editCount: meaningfulEdits.length, annotCount: annotations.length,
      editIds: new Set(meaningfulEdits.map((e) => e.id)),
      editFlagStates: new Map(meaningfulEdits.map((e) => [e.id, flagsOf(e)])),
      annotFlagStates: new Map(annotations.map((a) => [annotKey(a), flagsOf(a)])),
      formValues: { ...formValues },
    };
    const captured = latestSaveState.current;
    const saveName = pdfFileName(fileName);
    const exporting = intent === 'export' || intent === 'flatten';
    const toCloud = intent === 'cloud' || (intent === 'save' && cloudDocId);
    if (!silent) setFormProcessing(true);
    if (toCloud) setCloudSyncStatus('saving');
    const job = (async () => {
      try {
        const bytes = await generateSavedBytes({ flatten: intent === 'flatten' });
        if (!bytes) return false;
        const snapshot = { file: new File([bytes], saveName, { type: 'application/pdf' }),
          filePath: currentFilePath, cloudDocId, workingCopy, protectOriginal: hasDestructiveTextEdits(edits) };
        const result = await saveDocumentSnapshot(snapshot, { intent, saveFile: saveFileDialog });
        if (!result) return false;
        if (!viewerMounted.current || latestSaveState.current.file !== captured.file) return false;
        const unchanged = latestSaveState.current.revision === captured.revision;
        if (!exporting) {
          savedStateRef.current = baseline;
          savedNameRef.current = result.file.name;
          // A rename typed during a dialog remains pending, just like later text edits.
          setFileName((current) => current === fileName ? result.file.name : current);
          setCurrentFilePath(result.filePath);
          setCloudDocId(result.cloudDocId);
          setWorkingCopy(true);
          setInheritedDirty(false);
          refreshSavedState((v) => v + 1);
          // The editor exporter remains the authority while this view is mounted.
          // Its next handoff will include any changes made during this save.
          workspace?.remember({ ...result, dirty: !unchanged });
          if (!isDesktop() && !toCloud && usageCapApplies()) incrementUsage();
        }
        if (toCloud) setCloudSyncStatus('synced');
        if (!silent) showToast(exporting ? 'Copy exported. Your editable document is still open.'
          : toCloud ? 'Saved to cloud' : isDesktop() ? `Saved to ${result.filePath}` : 'PDF downloaded');
        // Save-and-close must stay open if a newer revision arrived during the dialog.
        return exporting || unchanged;
      } catch (err) {
        if (toCloud) setCloudSyncStatus('error');
        showToast({ message: `Could not save: ${err.message}. Your edits are still open.`, durationMs: 8000 });
        if (silent) throw err;
        return false;
      } finally {
        saveInFlight.current = null;
        if (!silent) setFormProcessing(false);
      }
    })();
    saveInFlight.current = { promise: job, intent };
    return job;
  }, [location.state?.file, meaningfulEdits, edits, annotations, formValues, fileName, cloudDocId,
    currentFilePath, workingCopy, commitUpdate, generateSavedBytes, saveFileDialog, setFileName, workspace, showToast]);

  latestSaveAction.current = saveRevision;
  const executeSaveAs = useCallback(() => saveRevision('saveAs'), [saveRevision]);
  const handleSave = useCallback(() => saveRevision('save'), [saveRevision]);
  const handleSaveToCloud = useCallback((options) => saveRevision('cloud', options), [saveRevision]);

  // Print — open the signed bytes in a hidden iframe and trigger the print
  // dialog. Works in both web and Electron renderer (Chromium PDF viewer).
  // Does NOT clear editor state — the user stays in-edit after printing.
  const executePrint = useCallback(async () => {
    const file = location.state?.file;
    if (!file) return;
    setFormProcessing(true);
    try {
      const savedBytes = await generateSavedBytes();
      if (!savedBytes) return;
      await printDocument(savedBytes);
    } catch (err) { showToast({ message: `Could not print: ${err.message}. Your document is still open.`, durationMs: 8000 }); }
    finally { setFormProcessing(false); }
  }, [location.state, generateSavedBytes, showToast]);

  // "Move to cloud" on the source chip — identical to Save to Cloud.
  const handleMoveToCloud = useCallback(() => handleSaveToCloud(), [handleSaveToCloud]);

  // Debounced auto-save back to cloud whenever this session is cloud-sourced
  // and has pending edits. Uses the same PUT path as manual save but in
  // silent mode — no viewer reload, no "Saved" toast, just the SourceChip
  // flipping to Saving… / Synced. Default idle window: 2 s.
  const documentRevision = useMemo(() => JSON.stringify([
    edits.filter((edit) => !isUntouchedDetection(edit)).map(flagsOf),
    annotations.map(flagsOf), formValues, fileName,
  ]), [edits, annotations, formValues, fileName]);
  useAutoSaveToCloud({
    enabled: docSource === 'cloud' && cloudState === 'available',
    hasUnsavedChanges,
    revision: documentRevision,
    onSave: () => handleSaveToCloud({ silent: true }),
  });

  // "Manage in cloud" on sync error → /cloud page.
  const handleViewCloud = useCallback(() => navigate('/cloud'), [navigate]);

  // All Save buttons, shortcuts and guards use the same active destination.
  const handleEditSave = handleSave;
  const handleEditSaveAs = executeSaveAs;
  const handleEditDownload = useCallback(() => saveRevision('export'), [saveRevision]);
  const handleEditPrint = useCallback(() => executePrint(), [executePrint]);

  const handleFlattenRequest = useCallback(() => {
    if (edits.length === 0 && annotations.length === 0 && !hasFormFields) return;
    setFlattenDialogOpen(true);
  }, [edits.length, annotations.length, hasFormFields]);

  const handleFlattenConfirm = useCallback(async () => {
    setFlattenDialogOpen(false);
    await saveRevision('flatten');
  }, [saveRevision]);

  const handleFlattenCancel = useCallback(() => {
    setFlattenDialogOpen(false);
  }, []);

  // Register save handlers for Electron menu (File > Save / Save As / Print / Find)
  useEffect(() => {
    viewerSaveRef.current = () => handleSave({});
    viewerSaveRef.saveAs = () => executeSaveAs();
    viewerSaveRef.print = () => executePrint();
    viewerSaveRef.find = () => setFindOpen(true);
    return () => {
      viewerSaveRef.current = null;
      viewerSaveRef.saveAs = null;
      viewerSaveRef.print = null;
      viewerSaveRef.find = null;
    };
  }, [handleSave, executeSaveAs, executePrint]);

  // Save-and-close (from main.js's window-close dialog). Run handleSave
  // and ALWAYS notify main when finished — even on save failure — so the
  // close path doesn't deadlock. Main's safety timeout (30s) also covers
  // pathological renderer crashes.
  useEffect(() => {
    if (!window.electronAPI?.onSaveAndClose) return;
    const unsubscribe = window.electronAPI.onSaveAndClose(async () => {
      let saved = false;
      try { saved = await handleSave({}); }
      finally { window.electronAPI.notifySaveComplete(saved === true); }
    });
    return unsubscribe;
  }, [handleSave]);

  /** Guard in-app navigation — shows warning if unsaved changes exist. */
  const guardedNavigate = useCallback((action) => {
    if (hasUnsavedChanges && !workspace) {
      setPendingNavAction(() => action);
      setShowUnsavedWarning(true);
    } else {
      action();
    }
  }, [hasUnsavedChanges, workspace]);

  const handleNavStay = useCallback(() => {
    setShowUnsavedWarning(false);
    setPendingNavAction(null);
  }, []);

  const handleNavLeave = useCallback(() => {
    setShowUnsavedWarning(false);
    setFlattenDialogOpen(false);
    clearEdits();
    if (pendingNavAction) pendingNavAction();
    setPendingNavAction(null);
  }, [pendingNavAction, clearEdits]);

  // --- Add image handler ---
  const handleAddImage = useCallback(async (file) => {
    if (!file) return;
    try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Could not read this image.'));
      reader.readAsDataURL(file);
    });

    // Normalize non-PNG/JPG to PNG via canvas
    let finalUrl = dataUrl;
    if (!file.type.includes('png') && !file.type.includes('jpeg') && !file.type.includes('jpg')) {
      const img = new window.Image();
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = dataUrl; });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      finalUrl = canvas.toDataURL('image/png');
    }

    // Get dimensions and fit within 200x200
    const img = new window.Image();
    await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = () => reject(new Error('This image could not be opened. Try PNG or JPEG.')); img.src = finalUrl; });
    const maxDim = 200;
    const scale = Math.min(maxDim / img.naturalWidth, maxDim / img.naturalHeight, 1);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;

    addEdit({
      type: 'image',
      pageNum: currentPage,
      x: (pageSize.width - w) / 2,
      y: (pageSize.height - h) / 2,
      width: w,
      height: h,
      content: finalUrl,
      styles: {},
    });
    setEditTool('select');
    } catch (err) { showToast({ message: err.message || 'This image could not be opened. Try PNG or JPEG.', durationMs: 6000 }); }
  }, [currentPage, pageSize, addEdit, setEditTool, showToast]);

  // --- Signature panel ---
  const handleSignatureCreated = useCallback((dataUrl) => {
    setSignatureDataUrl(dataUrl);
    setShowSignaturePanel(false);
    setSignaturePanelView(null);
    setEditTool('signature');
    setSignaturePlacementHint(true);
    // Auto-hide hint after 4 seconds
    setTimeout(() => setSignaturePlacementHint(false), 4000);
  }, [setEditTool]);

  const handleOpenSignaturePanel = useCallback(() => {
    // From the Sign dropdown's "+ New signature" affordance — jump straight
    // to Create. Guests hit this path too (no dropdown renders for them),
    // and the panel already defaults to Create when not signed in.
    setSignaturePanelView('create');
    setShowSignaturePanel(true);
  }, []);

  const handleSelectSavedSignature = useCallback((sig) => {
    setSignatureDataUrl(sig.dataUrl);
    setEditTool('signature');
    setSignaturePlacementHint(true);
    setTimeout(() => setSignaturePlacementHint(false), 4000);
  }, [setEditTool]);

  const handleDeleteSavedSignature = useCallback(async (id) => {
    try {
      await savedSigs.remove(id);
    } catch (err) {
      console.error('Delete signature failed:', err);
      showToast(err.message || 'Could not delete signature.');
    }
  }, [savedSigs, showToast]);

  const dragGeometry = useRef({ geometryForPage, zoom, updateEdits, endDrag });
  dragGeometry.current = { geometryForPage, zoom, updateEdits, endDrag };

  // Read the destination's actual geometry on every move. Page gaps, centered
  // pages of different sizes, rotation and scrolling all affect this mapping.
  const applyDragMove = useCallback((e) => {
    const ds = dragStateRef.current;
    if (!ds) return;
    const surfaces = [...document.querySelectorAll('[data-page]')];
    const underPointer = surfaces.find((el) => {
      const rect = el.getBoundingClientRect();
      return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    });
    const surface = underPointer || surfaces.find((el) => Number(el.dataset.page) === ds.currentPage);
    if (!surface) return;
    const targetPage = Number(surface.dataset.page);
    const { geometryForPage, zoom, updateEdits } = dragGeometry.current;
    const geometry = geometryForPage(targetPage);
    const point = pointOnPage(e.clientX, e.clientY, surface.getBoundingClientRect(), zoom, geometry);
    const height = geometry.baseHeight || geometry.height;
    const [left, bottom, right, top] = geometry.crop || [0, 0, geometry.baseWidth || geometry.width, height];
    const updates = new Map();
    const starts = ds.multiDrag ? ds.startPositions : new Map([[ds.editId, { x: ds.startX, y: ds.startY, width: ds.startW, height: ds.startH }]]);
    for (const [id, start] of starts) updates.set(id, {
      pageNum: targetPage,
      x: Math.max(left, Math.min(point.x - ds.grabX + start.x - ds.startX, right - start.width)),
      y: Math.max(height - top, Math.min(point.y - ds.grabY + start.y - ds.startY, height - bottom - start.height)),
    });
    updateEdits(updates);
    ds.currentPage = targetPage;
  }, []);

  // Called by EditLayer once the pointer crosses the drag threshold, with the
  // move event that crossed it. Everything here is synchronous: the grab
  // offset comes from the page rect captured at mousedown, and the window
  // listeners attach now rather than from an effect after the next commit —
  // a fast press → move → release otherwise lost its mouseup and left the
  // item following the bare pointer.
  const handleDragStart = useCallback((dragInfo, triggerEvent) => {
    const surface = document.querySelector(`[data-page="${dragInfo.pageNum}"]`);
    const startRect = dragInfo.startSurfaceRect || surface?.getBoundingClientRect();
    if (!startRect) return;
    dragTrackerRef.current?.stop();
    const { geometryForPage, zoom } = dragGeometry.current;
    const point = pointOnPage(dragInfo.startMouseX, dragInfo.startMouseY, startRect, zoom, geometryForPage(dragInfo.pageNum));
    const ds = { ...dragInfo, currentPage: dragInfo.pageNum,
      grabX: point.x - dragInfo.startX, grabY: point.y - dragInfo.startY };
    dragStateRef.current = ds;
    setViewerDragState(ds);
    dragTrackerRef.current = trackPointerDrag({
      onMove: applyDragMove,
      onEnd: (reason) => dragGeometry.current.endDrag(reason),
    });
    if (triggerEvent) applyDragMove(triggerEvent);
  }, [applyDragMove]);

  useEffect(() => () => {
    if (dragStateRef.current) dragGeometry.current.endDrag('unmount');
  }, []);

  const requiredEmpty = formFields.filter(
    (f) => f.required && (formValues[f.id] === '' || formValues[f.id] === false || formValues[f.id] == null)
  ).length;

  // For toolbar: show style options for the first selected text edit
  const selectedEdit = edits.find((e) => selectedEditIds.has(e.id));

  // Compute bounding rect of selected text edit for mobile popover positioning
  const [selectedEditRect, setSelectedEditRect] = useState(null);
  useEffect(() => {
    if (!selectedEdit || selectedEdit.type !== 'text') {
      setSelectedEditRect(null);
      return;
    }
    const el = document.querySelector('[style*="1.5px dashed"]');
    if (el) {
      setSelectedEditRect(el.getBoundingClientRect());
    } else {
      setSelectedEditRect(null);
    }
  }, [selectedEdit, edits, zoom]);

  // Form fields are now rendered by PdfAnnotationLayer (PDF.js DOM
  // AnnotationLayer for /Widget + inline-injected yellow+input for /FreeText).
  // FormFieldOverlay is retained as a fallback but no longer mounted here.

  // pdf.js text layer — document text selection/copy. Rendered first among
  // the overlay slots (via ScrollablePages' renderOverlay prop), directly
  // after PdfAnnotationLayer, so it sits below the mask/annotation/edit
  // layers in DOM order. Find-in-document highlights and clickable links
  // compose into this SAME slot (text layer, then highlights, then links) so
  // all three share the renderOverlay callback ScrollablePages exposes per
  // page — and, per the Task 2 stacking rule, all mount BEFORE the separate
  // Mask/Annotation/Edit overlay slots so an EditItem/annotation drawn over a
  // link rect still wins the click (see PdfLinkLayer.jsx for the full
  // rationale).
  const renderTextOverlay = useCallback(
    (pageNum) => (
      <>
        <PdfTextLayer pdfDoc={renderedPdfDoc} pageNum={pageNum} scale={zoom} interactive={textSelectable} />
        {renderSearchHighlightOverlay(pageNum)}
        <PdfLinkLayer pdfDoc={pdfDoc} pageNum={pageNum} scale={zoom} interactive={textSelectable} onGoToPage={goToPage} />
      </>
    ),
    [pdfDoc, renderedPdfDoc, zoom, textSelectable, renderSearchHighlightOverlay, goToPage]
  );

  const renderMaskOverlay = useCallback(
    (pageNum) => {
      const geometry = geometryForPage(pageNum);
      const size = { width: geometry.baseWidth || geometry.width, height: geometry.baseHeight || geometry.height };
      if (edits.length === 0 && annotations.length === 0) return null;
      return <MaskLayer edits={edits} annotations={annotations} pageNum={pageNum} geometry={geometry} pageWidth={size.width} pageHeight={size.height} zoom={zoom} selectedEditIds={selectedEditIds} />;
    },
    [edits, annotations, geometryForPage, zoom, selectedEditIds]
  );

  const renderEditOverlay = useCallback(
    (pageNum) => {
      const geometry = geometryForPage(pageNum);
      const size = { width: geometry.baseWidth || geometry.width, height: geometry.baseHeight || geometry.height };
      // When edit mode is off, hide unmodified detected edits — they're invisible
      // hit targets that would just sit uselessly over the canvas.
      const visibleEdits = editMode
        ? edits
        : edits.filter((e) => e.source !== 'detected' || e.modified || e.deleted);
      // Candidates are hit-targets only meaningful while actively editing —
      // same rule as unmodified detected edits above. visibleCandidates
      // derives which candidates are hidden because a live edit currently
      // claims them via sourceBlockId (Bug B fix) — a promoted block reverts
      // to selectable the instant its edit is deleted/undone, with no
      // separate restore step.
      const pageCandidates = editMode ? visibleCandidates(candidatesByPage.get(pageNum) || [], edits) : [];
      if (visibleEdits.length === 0 && pageCandidates.length === 0 && !editMode) return null;
      return (
        <EditLayer
          pageNum={pageNum} numPages={numPages} geometry={geometry} pageWidth={size.width} pageHeight={size.height} zoom={zoom}
          edits={visibleEdits} editTool={editMode ? editTool : null} editStyles={editStyles} selectedEditIds={editMode ? selectedEditIds : new Set()}
          candidates={pageCandidates} onPromoteCandidate={(block, intent) => handlePromoteCandidate(pageNum, block, intent)}
          onAddEdit={addEdit} onUpdateEdit={updateEdit} onUpdateEdits={updateEdits}
          onCommitUpdate={commitUpdate} onSelectEdit={handleSelectEditWrapped} onToggleSelect={toggleSelectEdit}
          onDeselectAll={handleDeselectAll} onDeleteEdit={deleteEdit}
          signatureDataUrl={signatureDataUrl}
          onOpenSignaturePanel={handleOpenSignaturePanel}
          onDragStart={handleDragStart}
          onSetTool={setEditTool}
          onSelectionChange={setEditingSelection}
          onSegmentStyleChange={handleSegmentStyleChange}
          annotationDrawing={!!activeTool}
        />
      );
    },
    [editMode, numPages, geometryForPage, zoom, edits, editTool, editStyles, selectedEditIds, candidatesByPage, handlePromoteCandidate, addEdit, updateEdit, updateEdits, commitUpdate, handleSelectEditWrapped, toggleSelectEdit, handleDeselectAll, deleteEdit, signatureDataUrl, handleOpenSignaturePanel, handleDragStart, setEditTool, handleSegmentStyleChange, activeTool]
  );

  const renderAnnotationOverlay = useCallback(
    (pageNum) => {
      const geometry = geometryForPage(pageNum);
      const size = { width: geometry.baseWidth || geometry.width, height: geometry.baseHeight || geometry.height };
      if (annotations.length === 0 && !editMode && !showAnnotToolbar) return null;
      return (
        <AnnotationLayer
          pageNum={pageNum} geometry={geometry} pageWidth={size.width} pageHeight={size.height} zoom={zoom}
          annotations={annotations} activeTool={(editMode || showAnnotToolbar) ? activeTool : null} color={color} opacity={opacity}
          strokeWidth={strokeWidth} fontSize={annotFontSize}
          onAddAnnotation={addAnnotation} onDeleteAnnotation={deleteAnnotation}
          selectable={annotSelectable}
          selectedGroupId={selectedGroupId}
          onSelectGroup={handleSelectGroup}
          onDeselectGroup={handleDeselectAll}
          onDeleteGroup={deleteGroup}
          onReplaceGroupAnnotations={replaceGroupAnnotations}
          onCommitAnnotationUpdate={commitAnnotationUpdate}
        />
      );
    },
    [editMode, showAnnotToolbar, geometryForPage, zoom, annotations, activeTool, color, opacity, strokeWidth, annotFontSize, addAnnotation, deleteAnnotation, annotSelectable, selectedGroupId, handleSelectGroup, handleDeselectAll, deleteGroup, replaceGroupAnnotations, commitAnnotationUpdate]
  );

  // --- Render ---
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
        <span className="ml-3 text-gray-600">Loading PDF…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-red-600">{error}</p>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-dark transition-colors">Back to Dashboard</button>
      </div>
    );
  }
  if (!pdfDoc) return null;

  return (
    <NavGuardContext.Provider value={{ guardedNavigate }}>
      {showPageOrganizer && !loading && pdfDoc && <PageOrganizer
        getFile={async () => new File([await generateSavedBytes()], fileName, { type: 'application/pdf' })}
        onClose={() => {
          setShowPageOrganizer(false);
          if (new URLSearchParams(location.search).get('panel') === 'pages') navigate('/viewer', { replace: true, state: location.state });
        }}
        onApply={async (bytes) => {
          setShowPageOrganizer(false);
          clearEdits(); restoreAnnotations([]); clearFormFields();
          navigate('/viewer', { replace: true, state: { file: new File([bytes], fileName, { type: 'application/pdf' }), filePath: currentFilePath, cloudDocId, dirty: true } });
        }}
      />}
    <div className="flex flex-col flex-1 min-h-0">
      <Toolbar
        currentPage={currentPage} numPages={numPages} zoom={zoom} isFitted={isFitted}
        onZoomIn={zoomIn} onZoomOut={zoomOut} onFitToWidth={handleFitToWidth}
        onResetZoom={resetZoom} onPrevPage={prevPage} onNextPage={nextPage}
        onGoToPage={goToPage} fileName={fileName} onFileNameChange={setFileName}
        file={location.state?.file}
        hideSave
        showAnnotate={false} annotateActive={false} onToggleAnnotate={null}
        editActive={false} onToggleEdit={null}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleEditSave}
        onDownload={handleEditDownload}
        onPrint={handleEditPrint}
        onBack={() => guardedNavigate(() => navigate('/'))}
        onSetZoom={(val) => { setIsFitted(false); setZoom(val); }}
        docSource={docSource}
        cloudSyncStatus={cloudSyncStatus}
        cloudAvailable={cloudState === 'available'}
        onMoveToCloud={cloudState === 'available' ? handleMoveToCloud : undefined}
        onViewCloud={handleViewCloud}
      />

      {edits.filter((edit) => edit.type === 'image' && selectedEditIds.has(edit.id) && !edit.deleted && !edit.locked).slice(0, 1).map((edit) => <ImageProperties key={edit.id} edit={edit} onDelete={deleteEdit} onUpdate={(id, updates) => { updateEdit(id, updates); requestAnimationFrame(commitUpdate); }} />)}
      {editTool === 'image' && imageDetection && <p role={imageDetection.error ? 'alert' : 'status'} className="px-4 py-2 text-sm text-gray-600 bg-gray-50 border-b">{imageDetection.busy ? 'Finding images…' : imageDetection.error || (edits.some((e) => e.type === 'image' && !e.deleted) ? 'Click an image to replace, crop, rotate or delete it.' : 'No embedded images found. Use Add image to insert a picture.')}</p>}
      {hasFormFields && (
        <FormToolbar fieldCount={fieldCount} highlightFields={highlightFields}
          onToggleHighlight={() => setHighlightFields((v) => !v)} onClearAll={clearFormFields}
          requiredEmpty={requiredEmpty} />
      )}

      <EditToolbar
          editTool={editTool} selectedEditIds={selectedEditIds}
          selectedEditType={selectedEdit?.type} editStyles={editStyles}
          canUndo={canUndoEdit} canRedo={canRedoEdit}
          hasUnsavedChanges={hasUnsavedChanges}
          onSetTool={setEditTool} onUpdateStyle={updateEditStyle}
          onDeleteSelected={deleteSelectedEdit} onAddImage={handleAddImage}
          onUndo={undoEdit} onRedo={redoEdit}
          onSave={handleEditSave}
          processing={formProcessing}
          onSaveAs={handleEditSaveAs}
          onDownload={handleEditDownload}
          onSaveToCloud={handleSaveToCloud}
          onPrint={handleEditPrint}
          docSource={docSource}
          cloudState={cloudState}
          cloudUpsell={cloudUpsell}
          onClose={null}
          signatureDataUrl={signatureDataUrl}
          onOpenSignaturePanel={handleOpenSignaturePanel}
          savedSigs={savedSigs}
          onSelectSavedSignature={handleSelectSavedSignature}
          onDeleteSavedSignature={handleDeleteSavedSignature}
          editCount={edits.length}
          selectedEditRect={selectedEditRect}
          // Annotation props for unified toolbar
          annotActiveTool={activeTool}
          annotColor={color}
          annotOpacity={opacity}
          annotStrokeWidth={strokeWidth}
          annotFontSize={annotFontSize}
          annotCanUndo={canUndo}
          annotCanRedo={canRedo}
          onSelectAnnotTool={selectTool}
          onSetAnnotColor={handleSetAnnotColor}
          onSetAnnotOpacity={handleSetAnnotOpacity}
          onSetAnnotStrokeWidth={handleSetAnnotStrokeWidth}
          onSetAnnotFontSize={setAnnotFontSize}
          onAnnotUndo={undo}
          onAnnotRedo={redo}
          annotationCount={annotations.length}
          onDeselectAll={handleDeselectAll}
          selectedAnnotation={selectedAnnotForToolbar}
          onDeleteSelectedAnnotation={() => deleteGroup(selectedGroupId)}
          selectionStyles={displaySelectionStyles}
          onUpdateSegmentStyle={handleUpdateSegmentStyle}
          hasDetectedContent={detectionRan}
          onFlatten={handleFlattenRequest}
          hasOverlays={edits.length > 0 || annotations.length > 0 || hasFormFields}
        />

      {/* No text found toast */}
      {noTextFound && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-center text-sm text-blue-800 shrink-0">
          No editable text found. This may be a scanned document — try OCR first.
        </div>
      )}

      <div className="flex flex-1 min-h-0 relative">
        {/* Signature placement hint — overlaid, never in flow. As an in-flow
            row it pushed the (vertically centered) page down, then pulled it
            back up the instant the click placed the signature or the 4s
            auto-hide fired, so the page jumped while the user was aiming
            and the signature appeared to land above the cursor. */}
        {signaturePlacementHint && editTool === 'signature' && (
          <div
            data-testid="signature-placement-hint"
            className="absolute top-3 left-1/2 -translate-x-1/2 z-40 pointer-events-none rounded-full shadow-md bg-amber-50 border border-amber-200 px-4 py-1.5 text-sm text-amber-800 whitespace-nowrap"
          >
            Click on the page to place your signature
          </div>
        )}
        {showDetectingOverlay && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg px-6 py-4 flex items-center gap-3">
              <Loader2 size={20} className="animate-spin text-brand-blue" />
              <span className="text-sm text-gray-600">Analyzing document...</span>
            </div>
          </div>
        )}
        <ThumbnailSidebar pdfDoc={pdfDoc} numPages={numPages} currentPage={currentPage} onPageSelect={goToPage} disabled={!showThumbs} />

        {/* Find bar floats above the scroll container / large-file page, as a
            sibling of both — never a descendant of either's click-to-deselect
            handler ([data-page] closest-check in ScrollablePages, .shadow-lg
            check in the large-file branch), so interacting with it never
            triggers a background deselect. */}
        <FindBar
          open={findOpen}
          onOpen={handleFindOpen}
          onClose={handleFindClose}
          onNavigate={setActiveFindIndex}
          results={findResults}
          activeIndex={activeFindIndex}
          query={findQuery}
          setQuery={setFindQuery}
          matchCase={findMatchCase}
          setMatchCase={setFindMatchCase}
          searching={findSearching}
        />

        {isLargeFile ? (
          <div ref={singlePageContainerRef} className="flex-1 overflow-auto bg-gray-100 flex flex-col items-center p-8"
            onClick={(e) => { if (!e.target.closest('.shadow-lg')) handleBackgroundClick(); }}>
            <div className="flex items-center gap-2 mb-4 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-xs text-amber-700">
              <FileText size={14} />
              <span>Single-page mode — Page {currentPage.toLocaleString()} of {numPages.toLocaleString()}</span>
            </div>
            <div className="shadow-lg relative">
              {/* Only divert annotation appearances off the canvas for genuine
                  interactive AcroForm widgets (hasFormFields). For every other
                  PDF — including FreeText "fillable" forms — the map is null, so
                  PDF.js paints all annotation /AP appearances onto the canvas
                  faithfully, exactly as stored (source of truth). */}
              <PageCanvas
                pdfDoc={renderedPdfDoc}
                pageNumber={currentPage}
                scale={zoom}
                className="bg-white"
                hideFormFields={hasFormFields}
                annotationCanvasMap={hasFormFields ? annotationCanvasMapRef.current : null}
                onRenderComplete={handleCanvasRenderComplete}
              />
              <PdfTextLayer pdfDoc={renderedPdfDoc} pageNum={currentPage} scale={zoom} interactive={textSelectable} />
              {renderSearchHighlightOverlay(currentPage)}
              <PdfLinkLayer pdfDoc={pdfDoc} pageNum={currentPage} scale={zoom} interactive={textSelectable} onGoToPage={goToPage} />
              {/* The DOM annotation layer (interactive widget inputs) is only
                  needed for genuine AcroForm fields. Non-form PDFs render
                  entirely on the canvas above — no overlay, no double-render. */}
              {hasFormFields && (
                <PdfAnnotationLayer
                  pdfDoc={pdfDoc}
                  pageNum={currentPage}
                  scale={zoom}
                  annotationCanvasMap={annotationCanvasMapRef.current}
                  canvasRenderToken={canvasRenderToken}
                  formFields={formFields}
                  formValues={formValues}
                  onFieldValueChange={setFormValue}
                />
              )}
              {(edits.length > 0 || annotations.length > 0) && (
                <MaskLayer edits={edits} annotations={annotations} pageNum={currentPage} geometry={currentGeometry} pageWidth={pageSize.width} pageHeight={pageSize.height} zoom={zoom} selectedEditIds={selectedEditIds} />
              )}
              {(annotations.length > 0 || editMode || showAnnotToolbar) && (
                <AnnotationLayer pageNum={currentPage} geometry={currentGeometry} pageWidth={pageSize.width} pageHeight={pageSize.height} zoom={zoom}
                  annotations={annotations} activeTool={(editMode || showAnnotToolbar) ? activeTool : null} color={color} opacity={opacity} strokeWidth={strokeWidth} fontSize={annotFontSize}
                  onAddAnnotation={addAnnotation} onDeleteAnnotation={deleteAnnotation}
                  selectable={annotSelectable}
                  selectedGroupId={selectedGroupId}
                  onSelectGroup={handleSelectGroup}
                  onDeselectGroup={handleDeselectAll}
                  onDeleteGroup={deleteGroup}
                  onReplaceGroupAnnotations={replaceGroupAnnotations}
                  onCommitAnnotationUpdate={commitAnnotationUpdate} />
              )}
              {(edits.length > 0 || editMode) && (() => {
                const visibleEdits = editMode
                  ? edits
                  : edits.filter((e) => e.source !== 'detected' || e.modified || e.deleted);
                const pageCandidates = editMode ? visibleCandidates(candidatesByPage.get(currentPage) || [], edits) : [];
                if (visibleEdits.length === 0 && pageCandidates.length === 0 && !editMode) return null;
                return <EditLayer pageNum={currentPage} numPages={numPages} geometry={currentGeometry} pageWidth={pageSize.width} pageHeight={pageSize.height} zoom={zoom}
                  edits={visibleEdits} editTool={editMode ? editTool : null} editStyles={editStyles} selectedEditIds={editMode ? selectedEditIds : new Set()}
                  candidates={pageCandidates} onPromoteCandidate={(block, intent) => handlePromoteCandidate(currentPage, block, intent)}
                  onAddEdit={addEdit} onUpdateEdit={updateEdit} onUpdateEdits={updateEdits}
                  onCommitUpdate={commitUpdate} onSelectEdit={handleSelectEditWrapped} onToggleSelect={toggleSelectEdit}
                  onDeselectAll={handleDeselectAll} onDeleteEdit={deleteEdit}
                  signatureDataUrl={signatureDataUrl}
                  onOpenSignaturePanel={handleOpenSignaturePanel}
                  onDragStart={handleDragStart}
                  onSetTool={setEditTool}
                  onSelectionChange={setEditingSelection}
                  onSegmentStyleChange={handleSegmentStyleChange}
                  annotationDrawing={!!activeTool} />;
              })()}
            </div>
          </div>
        ) : (
          <ScrollablePages
            ref={scrollPagesRef} pdfDoc={pdfDoc} numPages={numPages} zoom={zoom}
            renderedPdfDoc={renderedPdfDoc}
            pageWidth={displayedPageSize.width} pageHeight={displayedPageSize.height}
            onVisiblePageChange={handleVisiblePageChange}
            renderOverlay={renderTextOverlay}
            renderMaskOverlay={renderMaskOverlay}
            renderEditOverlay={renderEditOverlay}
            renderAnnotationOverlay={renderAnnotationOverlay}
            hideFormFields={hasFormFields}
            useDomAnnotations={hasFormFields}
            formFields={formFields}
            formValues={formValues}
            onFieldValueChange={setFormValue}
            onBackgroundClick={handleBackgroundClick}
          />
        )}
      </div>

      {showUnsavedWarning && (
        <UnsavedWarningDialog onStay={handleNavStay} onLeave={handleNavLeave} />
      )}
      {(removalPreview.pending || removalPreview.error) && <div role={removalPreview.error ? 'alert' : 'status'} className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 bg-white border rounded-lg shadow-lg px-4 py-3 text-sm">
        {removalPreview.error ? `Could not refresh the text background: ${removalPreview.error}. Your edits are still open.` : 'Preparing text background…'}
      </div>}
      {showSignaturePanel && (
        <SignaturePanel
          savedSigs={savedSigs}
          initialView={signaturePanelView}
          fieldKind="signature"
          onCreated={handleSignatureCreated}
          onClose={() => { setShowSignaturePanel(false); setSignaturePanelView(null); }}
        />
      )}
      <FlattenConfirmDialog
        open={flattenDialogOpen}
        editCount={edits.length}
        annotationCount={annotations.length}
        formFieldCount={formFields.length}
        onConfirm={handleFlattenConfirm}
        onCancel={handleFlattenCancel}
      />
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />

      {/* AI panel — slide-in from right. The panel itself owns the paywall UI
          for non-AI tiers, so we render the button unconditionally. */}
      <button
        type="button"
        onClick={() => setShowAiPanel(true)}
        aria-label="Open AI tools"
        className="fixed bottom-20 right-6 z-30 inline-flex items-center gap-1.5 rounded-full bg-indigo-600 text-white shadow-lg px-4 py-2 text-sm font-semibold hover:bg-indigo-700"
      >
        <Sparkles className="w-4 h-4" />
        AI
      </button>
      <AiPanel
        open={showAiPanel}
        onClose={() => setShowAiPanel(false)}
        file={location.state?.file}
        hideSave
        onAction={(action) => {
          const file = location.state?.file;
          if (!file || !action?.type) return;

          // --- A) IN-PLACE execution (delete_pages, extract_pages) ---
          if (action.type === 'delete_pages' || action.type === 'extract_pages') {
            const execute = async () => {
              const plan = action.type === 'delete_pages'
                ? planDeleteExecution(action, numPages)
                : planExtractExecution(action, numPages);
              if (plan.error) {
                const key = plan.error === 'delete_all' ? 'ai.error_delete_all' : 'ai.error_no_pages';
                showToast({ message: t(key), action: null, durationMs: 3000 });
                return;
              }
              try {
                const buf = await file.arrayBuffer();
                const bytes = await splitPDF(buf, plan.keepNums);
                const baseName = file.name.replace(/\.pdf$/i, '');
                const newFile = new File(
                  [bytes],
                  `${baseName}_edited.pdf`,
                  { type: 'application/pdf' },
                );
                navigate('/viewer', {
                  state: {
                    file: newFile,
                    aiUndoFile: file,
                    aiUndoSummary: buildUndoSummary(plan.summary),
                  },
                });
              } catch (err) {
                if (import.meta.env.DEV) console.error('AI execute failed:', err);
                showToast({ message: t('ai.error_execute'), action: null, durationMs: 3000 });
              }
            };
            setShowAiPanel(false);
            guardedNavigate(execute);
            return;
          }

          // --- B) PRE-FILL + confirm (compress, convert_to_image, merge_split) ---
          let dest = null;
          let extra = null;
          if (action.type === 'compress') {
            dest = '/compress';
            extra = { aiLevel: normalizeAiLevel(action.target) };
          } else if (action.type === 'convert_to_image') {
            dest = '/convert';
            extra = { aiFormat: normalizeAiFormat(action.target) };
          } else if (action.type === 'merge_split') {
            dest = '/split';
            extra = { aiGroups: action.target };
          }
          if (dest) {
            setShowAiPanel(false);
            guardedNavigate(() => navigate(dest, { state: { file, ...extra } }));
            return;
          }

          // --- C) NO EXECUTION (extract_text, extract_table, answer, no_action) ---
          // Inline result already rendered in AssistantTab. Nothing to do.
        }}
      />

      {/* Toast — supports plain string or { message, action } shape. */}
      {toastMessage && (() => {
        const isObj = typeof toastMessage === 'object' && toastMessage !== null;
        const message = isObj ? toastMessage.message : toastMessage;
        const action = isObj ? toastMessage.action : null;
        return (
          <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-3">
            <Check size={14} className="text-green-400" />
            <span>{message}</span>
            {action && (
              <button
                type="button"
                onClick={() => { dismissToast(); action.onClick(); }}
                className="ml-1 text-xs font-semibold text-indigo-300 hover:text-indigo-200 underline"
              >
                {action.label}
              </button>
            )}
          </div>
        );
      })()}
    </div>
    </NavGuardContext.Provider>
  );
}
